import type { ApiError } from 'src/types/common';
import type { KeyedMutator, SWRConfiguration } from 'swr';
import type { FilterSortParams, PaginationParams, PaginatedResponse } from 'src/types/pagination';

import { useRef, useMemo } from 'react';

import { fetcher } from 'src/utils/axios';

import { PAGINATED_SWR_OPTIONS } from 'src/config/swr';

import { DEFAULT_PAGE_SIZE } from 'src/types/pagination';

import { useSWRWithDebounce } from './use-debounced-swr';

/**
 * Configuration options for the paginated API hook
 * @template T - The type of items being paginated
 */
interface UsePaginatedApiOptions<T> {
  /** Base API endpoint for the paginated resource */
  endpoint: string | null;
  /** Filtering and sorting parameters */
  filterParams?: FilterSortParams;
  /** Pagination parameters */
  paginationParams?: PaginationParams;
  /** Additional query parameters to include in the request */
  additionalParams?: Record<string, string | number | string[] | number[]>;
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
  /** When false, do not show previous key's data when URL changes (list clears until new data loads) */
  keepPreviousData?: boolean;
  /** SWR deduping window in ms. Identical keys within this interval reuse the in-flight/cached request. */
  dedupingInterval?: number;
  /** When true, force page 1 in the request when the search term changes */
  resetPageOnSearchChange?: boolean;
}

/**
 * Return type for the paginated API hook
 * @template T - The type of items being paginated
 */
interface UsePaginatedApiReturn<T> {
  /** Array of paginated items */
  data: T[];
  /** Pagination metadata */
  pagination: PaginatedResponse<T>['meta'];
  /** Whether the request is currently loading */
  isLoading: boolean;
  /** Error object if the request failed */
  error: ApiError | null;
  /** Whether the request is currently revalidating */
  isValidating: boolean;
  /** Whether the result set is empty */
  isEmpty: boolean;
  /** Function to manually trigger a revalidation */
  mutate: KeyedMutator<PaginatedResponse<T>>;
  /** The request this hook currently wants an answer for. */
  requestKey: string | null;
  /** The request the returned `data` actually answers; trails `requestKey` in flight. */
  resultKey: string | null;
  extra?: Record<string, any>;
}

/**
 * A reusable hook for fetching paginated data from an API endpoint
 * @template T - The type of items in the paginated response
 * @param options - Configuration options for the API request
 * @returns Object containing paginated data, loading states, and utility functions
 *
 * @example
 * ```typescript
 * interface User { id: number; name: string; }
 *
 * const {
 *   data: users,
 *   pagination,
 *   isLoading,
 *   error
 * } = usePaginatedApi<User>({
 *   endpoint: '/api/users',
 *   filterParams: { search: 'john', sortField: 'name' },
 *   paginationParams: { page: 1, take: 20 }
 * });
 * ```
 */
export function usePaginatedApi<T>({
  endpoint,
  filterParams = {},
  paginationParams = { page: 1, take: DEFAULT_PAGE_SIZE },
  additionalParams = {},
  enabled = true,
  keepPreviousData,
  dedupingInterval,
  resetPageOnSearchChange = false,
}: UsePaginatedApiOptions<T>): UsePaginatedApiReturn<T> {
  const { page = 1, take = DEFAULT_PAGE_SIZE } = paginationParams;
  const { search, sortField, sortOrder, sortOptions } = filterParams;

  const previousSearchRef = useRef<string | undefined>(search);

  const swrConfig: SWRConfiguration = useMemo(() => {
    const overrides: Partial<SWRConfiguration> = {};
    if (keepPreviousData !== undefined) {
      overrides.keepPreviousData = keepPreviousData;
    }
    if (dedupingInterval !== undefined) {
      overrides.dedupingInterval = dedupingInterval;
    }
    return { ...PAGINATED_SWR_OPTIONS, ...overrides };
  }, [keepPreviousData, dedupingInterval]);

  const searchChanged = previousSearchRef.current !== search;
  const pageForRequest = resetPageOnSearchChange && searchChanged ? 1 : (page ?? 1);

  const url = useMemo((): string | null => {
    if (!endpoint || !enabled) {
      return null;
    }

    const queryParams = new URLSearchParams();
    
    // Add basic pagination params
    queryParams.append('page', pageForRequest.toString());
    queryParams.append('take', take?.toString() || DEFAULT_PAGE_SIZE.toString());
    
    // Add additional params, handling arrays properly
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // For arrays, append each value separately
        value.forEach((item) => {
          queryParams.append(key, String(item));
        });
      } else if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    if (search?.trim()) {
      queryParams.append('search', search.trim());
    }

    if (sortField) {
      queryParams.append('sortField', sortField);
      queryParams.append('order', sortOrder?.toUpperCase() || 'DESC');
    }

    if (sortOptions) {
      // ?sortFields[0][field]=country&sortFields[0][order]=ASC&sortFields[1][field]=city&sortFields[1][order]=ASC
      sortOptions.forEach((field, index) => {
        queryParams.append(`sortOptions[${index}][field]`, field.field);
        queryParams.append(`sortOptions[${index}][order]`, field.order);
      });
    }

    return `${endpoint}?${queryParams.toString()}`;
  }, [endpoint, pageForRequest, take, search, sortField, sortOrder, sortOptions, additionalParams, enabled]);

  const textChanged = searchChanged;
  const { data, error, isLoading, isValidating, mutate, activeKey } = useSWRWithDebounce<
    PaginatedResponse<T>,
    ApiError
  >({
    fetchUrl: url,
    fetchFn: fetcher,
    swrConfig,
    debounceDelay: textChanged ? 1500 : 0,
  });

  setTimeout(() => {
    previousSearchRef.current = search;
  });

  const memoizedData = useMemo(() => [...(data?.data ?? [])], [data]);

  const memoizedValue = useMemo(
    (): Omit<UsePaginatedApiReturn<T>, 'data'> => ({
      pagination: data?.meta || {
        page: page || 1,
        take: take || DEFAULT_PAGE_SIZE,
        itemCount: 0,
        pageCount: 1,
        hasPreviousPage: (page || 1) > 1,
        hasNextPage: false,
      },
      isLoading,
      error: error || null,
      isValidating,
      isEmpty: !isLoading && !data?.data?.length,
      mutate,
      requestKey: url,
      resultKey: activeKey,
      extra:  { ...data, data: undefined, meta: undefined },
    }),
    [data, error, isLoading, isValidating, mutate, page, take, url, activeKey]
  );

  return {
    ...memoizedValue,
    data: memoizedData,
  };
}
